import React, { useState, useEffect, useRef, useCallback } from 'react';
import { criteria } from '../data/glasgowData';

const EMPTY = { eye: null, verbal: null, motor: null, pupils: null };

const MAX = { eye: 4, verbal: 5, motor: 6, pupils: 2 };

const Calculator = ({ onSelectionsChange }) => {
  const [selections, setSelections] = useState(EMPTY);
  const rootRef = useRef(null);
  const completionTrackedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const read = (keys, max) => {
      for (const k of keys) {
        const raw = params.get(k);
        if (raw === null) continue;
        if (raw === 'NT') return 'NT';
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 1 && n <= max) return n;
      }
      return null;
    };

    const next = {
      eye: read(['e', 'o', 'eye'], MAX.eye),
      verbal: read(['v', 'verbal'], MAX.verbal),
      motor: read(['m', 'motor'], MAX.motor),
      pupils: null,
    };

    const pupils = params.get('pupil') || params.get('pupils');
    if (pupils === '0' || pupils === 'reactive' || pupils === 'both-reactive') next.pupils = 0;
    else if (pupils === '1' || pupils === 'unilateral') next.pupils = 1;
    else if (pupils === '2' || pupils === 'bilateral') next.pupils = 2;

    if (Object.values(next).some((v) => v !== null)) {
      setSelections(next);
      if (onSelectionsChange) onSelectionsChange(next);
    }
  }, []);

  useEffect(() => {
    const complete = selections.eye !== null && selections.verbal !== null && selections.motor !== null;
    if (!complete) {
      completionTrackedRef.current = false;
      return;
    }
    if (completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    window.eidocAnalytics?.capture?.('calculator_used', { calculator: 'glasgow_adult' });
  }, [selections.eye, selections.verbal, selections.motor]);

  // Severidade é derivada no readout — aqui só reportamos as seleções,
  // para não haver duas fontes disputando o mesmo estado.
  const commit = useCallback(
    (next) => {
      setSelections(next);
      if (onSelectionsChange) onSelectionsChange(next);
    },
    [onSelectionsChange]
  );

  const handleSelect = useCallback(
    (category, value) => {
      setSelections((prev) => {
        const next = { ...prev, [category]: prev[category] === value ? null : value };
        if (onSelectionsChange) onSelectionsChange(next);
        return next;
      });
    },
    [onSelectionsChange]
  );

  const handleClear = useCallback(() => commit(EMPTY), [commit]);

  // Teclado: número seleciona no grupo EM FOCO (não nos três de uma vez).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClear();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const n = parseInt(e.key, 10);
      if (Number.isNaN(n)) return;

      const group = document.activeElement?.closest?.('[data-cat]');
      const cat = group?.dataset.cat;
      if (!cat) return;

      const min = cat === 'pupils' ? 0 : 1;
      if (n < min || n > MAX[cat]) return;

      e.preventDefault();
      handleSelect(cat, n);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClear, handleSelect]);

  const renderKey = (category, opt) => {
    const isSelected = selections[category] === opt.val;
    const isNT = opt.val === 'NT';

    return (
      <button
        key={opt.val}
        type="button"
        onClick={() => handleSelect(category, opt.val)}
        className={`key${isNT ? ' key--nt' : ''}`}
        aria-pressed={isSelected}
      >
        <span className="key__lb">{isNT ? 'Não Testável' : opt.label}</span>
        <span className="key__pt mono">{opt.val}</span>
        <span className="key__ds">{opt.desc}</span>
      </button>
    );
  };

  const renderSection = (category, title, letter, range) => {
    const options = criteria.adult[category];
    const numeric = options.filter((o) => o.val !== 'NT').length;

    return (
      <fieldset className="grp" data-cat={category}>
        <legend className="grp__lg">
          <span className="grp__n mono">{letter}</span>
          <span>{title}</span>
          <span className="grp__r mono">{range}</span>
        </legend>
        <div className="grp__track" data-filled={selections[category] !== null} />
        <div className="keys" data-n={numeric}>
          {options.map((opt) => renderKey(category, opt))}
        </div>
      </fieldset>
    );
  };

  const pupilOptions = [
    { val: 0, label: 'Ambas reativas', desc: 'Pupilas isocóricas e fotorreagentes' },
    { val: 1, label: 'Uma não reativa', desc: 'Anisocoria ou reatividade assimétrica' },
    { val: 2, label: 'Ambas não reativas', desc: 'Ausência bilateral de reatividade à luz' },
  ];

  return (
    <div ref={rootRef}>
      {renderSection('eye', 'Abertura Ocular', 'O', '1–4')}
      {renderSection('verbal', 'Resposta Verbal', 'V', '1–5')}
      {renderSection('motor', 'Resposta Motora', 'M', '1–6')}

      <fieldset className="grp" data-cat="pupils">
        <legend className="grp__lg">
          <span className="grp__n mono">P</span>
          <span>Reatividade Pupilar</span>
          <span className="grp__r mono">Opcional</span>
        </legend>
        <div className="grp__track" data-filled={selections.pupils !== null} />
        <div className="keys" data-n={3}>
          {pupilOptions.map((opt) => renderKey('pupils', opt))}
        </div>
      </fieldset>

      <p className="shortcuts mono">
        Número seleciona no grupo em foco · Tab avança de grupo · Esc limpa · Ctrl+C copia o registro
      </p>

      <button type="button" onClick={handleClear} className="btn">
        Limpar avaliação
      </button>
    </div>
  );
};

export default Calculator;
